import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { hotelsApi, shiftsApi, notificationsApi, usersApi } from '../../services/api';

const SEVERITY_STYLE = {
    critical: { color: '#c0392b', bg: 'rgba(192,57,43,0.08)', icon: '🔴' },
    warning: { color: '#b8860b', bg: 'rgba(184,134,11,0.08)', icon: '🟠' },
    info: { color: 'var(--accent)', bg: 'rgba(0,0,0,0.03)', icon: '🔵' },
};

const NAV = [
    { id: 'dashboard', icon: '📊', label: 'Dashboard' },
    { id: 'multigrid', icon: '🏢', label: 'Multi-Hotel Grid', roles: ['superadmin'] },
    
    // Section: Bookings
    { id: 'checkin', icon: '🛎️', label: 'Check-in', section: 'Bookings' },
    { id: 'checkout', icon: '🚪', label: 'Check-out', section: 'Bookings' },
    
    // Section: Reservations
    { id: 'prebooking', icon: '📅', label: 'Pre-booking', section: 'Reservations' },
    { id: 'bulkbooking', icon: '👥', label: 'Bulk / Group Booking', section: 'Reservations' },
    { id: 'reservations', icon: '📋', label: 'Reservations', section: 'Reservations' },
    { id: 'calendar', icon: '🗓️', label: 'Calendar View', section: 'Reservations' },
    
    // Section: Guest Relations
    { id: 'guests', icon: '👥', label: 'Guest Directory', section: 'Guest Relations' },
    { id: 'followups', icon: '📞', label: 'Lead Follow-ups', section: 'Guest Relations' },
    
    // Section: General
    { id: 'expenses', icon: '💼', label: 'Expenses' },
    { id: 'inventory', icon: '📦', label: 'Inventory & Stock', section: 'Operations' },
    { id: 'laundry', icon: '🧺', label: 'Laundry', section: 'Operations' },
    { id: 'shifts', icon: '🕒', label: 'Cash Management' },
    { id: 'reports', icon: '📈', label: 'Reports & Analytics', section: 'Oversight' },
    { id: 'nightaudit', icon: '🌙', label: 'Night Audit', section: 'Oversight', roles: ['superadmin', 'manager', 'staff'] },
    { id: 'guestregister', icon: '📋', label: 'Guest Register', section: 'Oversight' },
    { id: 'draweraudit', icon: '🧾', label: 'Drawer Audit', section: 'Oversight', roles: ['superadmin', 'manager'] },
];

export default function Layout({ page, setPage, hotel, setHotel, children }) {
    const { user, logout } = useAuth();
    const confirm = useConfirm();
    const [allHotels, setAllHotels] = useState([]);
    const [showSwitcher, setShowSwitcher] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwMsg, setPwMsg] = useState(null); // { type: 'error'|'success', text }
    const [pwSaving, setPwSaving] = useState(false);

    const closeChangePassword = () => {
        setShowChangePassword(false);
        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPwMsg(null);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwMsg(null);
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            setPwMsg({ type: 'error', text: 'New password and confirmation do not match.' });
            return;
        }
        if (pwForm.newPassword.length < 6) {
            setPwMsg({ type: 'error', text: 'New password must be at least 6 characters.' });
            return;
        }
        setPwSaving(true);
        try {
            await usersApi.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
            setPwMsg({ type: 'success', text: 'Password updated. Use it next time you sign in.' });
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setPwMsg({ type: 'error', text: err.message || 'Failed to update password.' });
        }
        setPwSaving(false);
    };

    // Tracks the latest `hotel` prop so the async callback below never checks a stale
    // value — see the fix note in the effect for why this is necessary.
    const hotelRef = useRef(hotel);
    useEffect(() => { hotelRef.current = hotel; }, [hotel]);

    useEffect(() => {
        if (user?.role === 'superadmin' || user?.role === 'manager' || showSwitcher) {
            hotelsApi.list().then(rows => {
                if (user?.role === 'superadmin' || user?.role === 'manager') {
                    setAllHotels([{ id: 'all', name: '🏢 ALL PROPERTIES', city: 'Global Network', color: 'var(--accent)' }, ...rows]);
                } else {
                    setAllHotels(rows);
                }

                // If no hotel selected, or first load: read hotelRef.current (not the `hotel`
                // prop directly) — `hotel` isn't a dependency of this effect, so a plain
                // closure over it would always see the value from the render that created
                // this effect (typically null, right after login), even though `hotel` is
                // very likely already set correctly by the time this network call resolves.
                // Checking the stale value here overwrote every manager/superadmin's real
                // hotel with the 'ALL PROPERTIES' placeholder shortly after every login.
                if (!hotelRef.current && (user?.role === 'superadmin' || user?.role === 'manager')) {
                   setHotel({ id: 'all', name: '🏢 ALL PROPERTIES', city: 'Global Network', color: 'var(--accent)' });
                   setPage('multigrid');
                }
            });
        }
        // setHotel/setPage are stable React state setters — safe to list without
        // affecting when this effect re-runs. `hotel` itself is deliberately NOT
        // a dependency; see the comment above on why.
    }, [user, showSwitcher, setHotel, setPage]);

    const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U';

    const [activeShift, setActiveShift] = useState(null);
    const [elapsedStr, setElapsedStr] = useState('');
    const [checkedShiftForHotel, setCheckedShiftForHotel] = useState(null);

    // Initial check + Polling for Shift enforcement
    useEffect(() => {
        if (!hotel || !user) {
            setActiveShift(null);
            return;
        }
        
        const fetchShift = async () => {
            try {
                const allShifts = await shiftsApi.list(hotel.id);
                const mine = allShifts.find(s => s.status === 'active' && String(s.user_id) === String(user.id));
                setActiveShift(mine || false); // false = loaded but no shift, null = still loading
                
                if (checkedShiftForHotel !== hotel.id) {
                    setCheckedShiftForHotel(hotel.id);
                }
            } catch (err) {
                console.error('Failed to fetch shift status:', err);
                // A transient network blip on this 30s poll must not masquerade as a
                // confirmed "no active shift" — that would unmount whatever page is
                // currently showing (e.g. a check-in/checkout form the user is mid-way
                // through filling in) and silently destroy their unsaved work. Only
                // treat it as "no shift" if we don't already know otherwise; a
                // previously confirmed active shift is left in place and the next
                // poll will retry.
                setActiveShift(prev => (prev ? prev : false));
            }
        };

        fetchShift(); // Initial fetch
        const int = setInterval(fetchShift, 30000); // Poll every 30s
        return () => clearInterval(int);
    }, [hotel, user, page, checkedShiftForHotel, setPage]);

    // Live Shift Timer Formatting
    useEffect(() => {
        if (!activeShift) {
            setElapsedStr('');
            return;
        }
        const updateTimer = () => {
            const startStr = activeShift.startAt || activeShift.start_at;
            if (!startStr) return;
            // Safari/Date.parse compatibility
            const tStart = new Date(startStr.replace(' ', 'T')).getTime();
            if (isNaN(tStart)) return;
            const diffMs = Date.now() - tStart;
            const s = Math.floor(diffMs / 1000) % 60;
            const m = Math.floor(diffMs / 60000) % 60;
            const h = Math.floor(diffMs / 3600000);
            setElapsedStr(`${h.toString().padStart(2,'0')}:${m.toString().padStart(2,'0')}:${s.toString().padStart(2,'0')}`);
        };
        updateTimer();
        const int2 = setInterval(updateTimer, 1000);
        return () => clearInterval(int2);
    }, [activeShift]);

    // Notifications — computed alerts (low stock, follow-ups due, overdue checkouts,
    // etc.), polled independently of the shift check above since they're relevant
    // regardless of role/shift state.
    const [notifications, setNotifications] = useState([]);
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    useEffect(() => {
        if (!hotel || hotel.id === 'all') { setNotifications([]); return; }
        const fetchNotifications = () => {
            notificationsApi.list(hotel.id)
                .then(setNotifications)
                .catch(err => console.error('Failed to fetch notifications:', err));
        };
        fetchNotifications();
        const int = setInterval(fetchNotifications, 60000); // Poll every 60s
        return () => clearInterval(int);
    }, [hotel]);

    const goToNotification = (n) => {
        setShowNotifPanel(false);
        if (n.page) setPage(n.page);
    };

    // Optimistically flip `read` locally so the UI responds instantly, then persist —
    // if the server call fails, the next 60s poll will just re-sync to the true state.
    const markNotificationsRead = (ids) => {
        if (!ids.length) return;
        setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
        notificationsApi.markRead(ids).catch(err => console.error('Failed to mark notifications read:', err));
    };
    const markOneRead = (id, e) => {
        e.stopPropagation(); // don't also trigger the row's navigate-on-click
        markNotificationsRead([id]);
    };
    const markAllRead = () => {
        markNotificationsRead(notifications.filter(n => !n.read).map(n => n.id));
    };

    const [mobileOpen, setMobileOpen] = useState(false);
    useEffect(() => { setMobileOpen(false); }, [page]); // Auto-close on nav

    return (
        <div className="app-shell">
            {/* MOBILE OVERLAY */}
            <div className={`sidebar-overlay ${mobileOpen ? 'mobile-open' : ''}`} onClick={() => setMobileOpen(false)} />

            {/* SIDEBAR */}
            <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
                <div className="sidebar-logo">
                    <div className="logo-name">PAR Grand Hotel</div>
                    <div className="logo-sub">Hotel Management</div>
                </div>

                {/* Hotel selector */}
                {user?.role === 'superadmin' ? (
                    <div style={{ padding: '12px 14px' }}>
                        <div
                            className="hotel-pill"
                            onClick={() => setShowSwitcher(v => !v)}
                            style={{ borderColor: showSwitcher ? 'var(--accent)' : undefined }}
                        >
                            <div className="hp-label">Active Property ▾</div>
                            <div className="hp-name">{hotel?.name || '— Select Hotel —'}</div>
                            {hotel && <div className="hp-city">{hotel.city} · {hotel.tag}</div>}
                        </div>
                        {showSwitcher && (
                            <div className="hotel-switcher">
                                {allHotels.map(h => (
                                    <div
                                        key={h.id}
                                        className={`hotel-switcher-item ${hotel?.id === h.id ? 'active' : ''}`}
                                        onClick={() => { 
                                            setHotel(h); 
                                            setShowSwitcher(false); 
                                            if (h.id === 'all') setPage('multigrid');
                                            else if (page === 'multigrid') setPage('dashboard');
                                        }}
                                    >
                                        <div className="hotel-dot" style={{ background: h.color || 'var(--gold)' }} />
                                        <div>
                                            <div style={{ fontWeight: 800, fontSize: 13 }}>{h.name}</div>
                                            <div style={{ fontSize: 10, color: 'var(--muted)' }}>{h.city}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : hotel ? (
                    <div style={{ padding: '12px 14px' }}>
                        <div className="hotel-pill" style={{ cursor: 'default' }}>
                            <div className="hp-label">Your Property</div>
                            <div className="hp-name">{hotel.name}</div>
                            <div className="hp-city">{hotel.city} · {hotel.tag}</div>
                        </div>
                    </div>
                ) : null}

                {/* Navigation */}
                <nav className="nav">
                    {(() => {
                        const filteredNav = NAV.filter(item => !item.roles || item.roles.includes(user?.role));
                        const sections = [];
                        filteredNav.forEach(item => {
                            if (item.section && !sections.includes(item.section)) {
                                sections.push(item.section);
                            }
                        });

                        return (
                            <>
                                {/* No Section (Dashboard) */}
                                {filteredNav.filter(n => !n.section).map(item => (
                                    <div
                                        key={item.id}
                                        id={`nav-${item.id}`}
                                        className={`nav-item ${page === item.id ? 'active' : ''}`}
                                        onClick={() => setPage(item.id)}
                                    >
                                        <span className="nav-icon">{item.icon}</span>
                                        {item.label}
                                    </div>
                                ))}

                                {/* Grouped Sections */}
                                {sections.map(section => (
                                    <React.Fragment key={section}>
                                        <div className="nav-section">{section}</div>
                                        {filteredNav.filter(n => n.section === section).map(item => (
                                            <div
                                                key={item.id}
                                                id={`nav-${item.id}`}
                                                className={`nav-item ${page === item.id ? 'active' : ''}`}
                                                onClick={() => setPage(item.id)}
                                            >
                                                <span className="nav-icon">{item.icon}</span>
                                                {item.label}
                                            </div>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </>
                        );
                    })()}
                    {(user?.role === 'superadmin' || user?.role === 'manager') && (
                        <>
                            <div className="nav-section">Admin Settings</div>
                            <div
                                id="nav-hotelmanagement"
                                className={`nav-item ${
                                    ['hotelmanagement','hotels','users','roomtypes','rooms','billsettings'].includes(page) ? 'active' : ''
                                }`}
                                onClick={() => setPage('hotelmanagement')}
                            >
                                <span className="nav-icon">🏢</span>
                                Hotel Management
                            </div>
                            {user?.role === 'superadmin' && (
                                <div
                                    id="nav-supertools"
                                    className={`nav-item ${page === 'supertools' ? 'active' : ''}`}
                                    onClick={() => setPage('supertools')}
                                >
                                    <span className="nav-icon">🛡️</span>
                                    Data Maintenance
                                </div>
                            )}
                            {user?.role === 'superadmin' && (
                                <div
                                    id="nav-documents"
                                    className={`nav-item ${page === 'documents' ? 'active' : ''}`}
                                    onClick={() => setPage('documents')}
                                >
                                    <span className="nav-icon">📁</span>
                                    Documents
                                </div>
                            )}
                        </>
                    )}
                </nav>

                {/* Footer */}
                <div className="sidebar-foot">
                    <div className="user-info">
                        <div className="user-avatar">{initials}</div>
                        <div>
                            <div className="user-name">{user?.name}</div>
                            <div className="user-role">{user?.role}</div>
                        </div>
                    </div>
                    <button className="btn-ghost" style={{ width: '100%', marginBottom: 8, fontSize: 12 }} onClick={() => setShowChangePassword(true)}>
                        🔑 Change Password
                    </button>
                    <button className="btn-logout" id="btn-logout" onClick={async () => {
                        if (activeShift && user?.role === 'staff') {
                            const force = await confirm({
                                icon: '🕒',
                                title: 'Active Shift',
                                body: 'You have an active shift. Do you want to sign out WITHOUT closing the drawer/handing over cash?',
                                confirmText: 'Sign Out Anyway',
                                danger: true,
                            });
                            if (!force) {
                                if (page !== 'shifts') setPage('shifts');
                                return;
                            }
                        }
                        logout();
                        window.location.reload(); // Ensure clean slate
                    }}>Sign Out</button>
                </div>
            </aside>

            {/* MAIN */}
            <div className="main-area">
                <div className="top-bar">
                    <div className="hamburger" onClick={() => setMobileOpen(true)}>☰</div>
                    <div className="top-bar-title">
                    {['hotelmanagement','hotels','users','roomtypes','rooms','billsettings'].includes(page) ? '🏢 Hotel Management' :
                     page === 'supertools' ? '🛡️ Super Admin Tools' :
                     page === 'documents' ? '📁 Documents' :
                     (NAV.find(n => n.id === page)?.icon + ' ' + NAV.find(n => n.id === page)?.label)}
                    </div>
                    {hotel && (
                        <div className="top-bar-sub">
                            <span style={{
                                display: 'inline-block',
                                width: 8, height: 8,
                                borderRadius: '50%',
                                background: hotel.color,
                                marginRight: 6,
                            }} />
                            {hotel.name} · {hotel.city}
                            {activeShift && elapsedStr && (
                                <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 16, padding: '4px 10px', background: 'rgba(46, 204, 113, 0.1)', borderRadius: 20, border: '1px solid rgba(46, 204, 113, 0.3)', color: 'var(--success)' }}>
                                    <span style={{ fontSize: 10, marginRight: 6 }}>● LIVE SHIFT</span>
                                    <span style={{ fontWeight: 800, fontSize: 13, fontFamily: 'monospace' }}>{elapsedStr}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {hotel && hotel.id !== 'all' && (
                        <div style={{ position: 'relative', marginLeft: hotel ? 16 : 'auto' }}>
                            <button
                                onClick={() => setShowNotifPanel(v => !v)}
                                style={{
                                    position: 'relative', border: '1px solid var(--border)', background: showNotifPanel ? 'var(--s1)' : 'transparent',
                                    borderRadius: 8, width: 36, height: 36, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                                title="Notifications"
                            >
                                🔔
                                {notifications.filter(n => !n.read).length > 0 && (
                                    <span style={{
                                        position: 'absolute', top: -4, right: -4, background: notifications.some(n => !n.read && n.severity === 'critical') ? '#c0392b' : '#b8860b',
                                        color: '#fff', borderRadius: 10, minWidth: 18, height: 18, fontSize: 10, fontWeight: 800,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', border: '2px solid var(--s1)'
                                    }}>
                                        {notifications.filter(n => !n.read).length > 9 ? '9+' : notifications.filter(n => !n.read).length}
                                    </span>
                                )}
                            </button>
                            {showNotifPanel && (
                                <>
                                    <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={() => setShowNotifPanel(false)} />
                                    <div style={{
                                        position: 'absolute', top: 44, right: 0, width: 360, maxHeight: 440, overflowY: 'auto',
                                        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 999
                                    }}>
                                        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <span style={{ fontWeight: 700, fontSize: 13 }}>
                                                Notifications {notifications.filter(n => !n.read).length > 0 && `(${notifications.filter(n => !n.read).length} unread)`}
                                            </span>
                                            {notifications.some(n => !n.read) && (
                                                <button
                                                    onClick={markAllRead}
                                                    style={{ border: 'none', background: 'transparent', color: 'var(--accent)', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0 }}
                                                >
                                                    Mark all read
                                                </button>
                                            )}
                                        </div>
                                        {notifications.length === 0 ? (
                                            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
                                                🎉 Nothing needs your attention right now.
                                            </div>
                                        ) : (
                                            notifications.map(n => {
                                                const s = SEVERITY_STYLE[n.severity] || SEVERITY_STYLE.info;
                                                return (
                                                    <div
                                                        key={n.id}
                                                        onClick={() => goToNotification(n)}
                                                        style={{
                                                            padding: '10px 16px', borderBottom: '1px solid var(--border)', cursor: n.page ? 'pointer' : 'default',
                                                            background: n.read ? 'transparent' : s.bg,
                                                            opacity: n.read ? 0.55 : 1,
                                                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8
                                                        }}
                                                    >
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: n.read ? 'var(--muted)' : s.color }}>
                                                                <span>{s.icon}</span>{n.title}
                                                            </div>
                                                            <div style={{ fontSize: 12, color: 'var(--text)', marginTop: 2 }}>{n.message}</div>
                                                        </div>
                                                        {!n.read && (
                                                            <button
                                                                title="Mark as read"
                                                                onClick={(e) => markOneRead(n.id, e)}
                                                                style={{
                                                                    flexShrink: 0, border: '1px solid var(--border)', background: 'var(--bg)', borderRadius: 6,
                                                                    width: 24, height: 24, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                                }}
                                                            >
                                                                ✓
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
                <div className="page-content">
                    {/* Shift Guard: ONLY enforce for STAFF users. Admins/Managers can always view. */}
                    {(activeShift === null && user?.role === 'staff') && ['checkin','checkout','prebooking','reservations','expenses'].includes(page) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 16 }}>
                            <div style={{ fontSize: 36 }}>⏳</div>
                            <div style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 600 }}>Checking shift status…</div>
                        </div>
                    ) : (!activeShift && user?.role === 'staff') && ['checkin','checkout','prebooking','reservations','expenses'].includes(page) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh', gap: 20, padding: 32 }}>
                            <div style={{ fontSize: 64 }}>🔐</div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--danger)', marginBottom: 8 }}>No Active Shift</div>
                                <div style={{ fontSize: 14, color: 'var(--muted2)', maxWidth: 380, lineHeight: 1.6 }}>
                                    Staff members must start a shift before accessing operations.
                                </div>
                            </div>
                            <button
                                className="btn btn-gold"
                                style={{ padding: '14px 36px', fontSize: 15, fontWeight: 700 }}
                                onClick={() => setPage('shifts')}
                            >
                                🕒 Start Your Shift
                            </button>
                        </div>
                    ) : (
                        children
                    )}
                </div>
            </div>

            {showChangePassword && (
                <div className="overlay" onClick={closeChangePassword}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
                        <div className="modal-title">Change Password</div>
                        <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                            <div className="field">
                                <label>Current Password</label>
                                <input type="password" required autoFocus
                                    value={pwForm.currentPassword}
                                    onChange={e => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} />
                            </div>
                            <div className="field">
                                <label>New Password</label>
                                <input type="password" required minLength={6}
                                    value={pwForm.newPassword}
                                    onChange={e => setPwForm(f => ({ ...f, newPassword: e.target.value }))} />
                            </div>
                            <div className="field">
                                <label>Confirm New Password</label>
                                <input type="password" required minLength={6}
                                    value={pwForm.confirmPassword}
                                    onChange={e => setPwForm(f => ({ ...f, confirmPassword: e.target.value }))} />
                            </div>
                            {pwMsg && (
                                <div style={{
                                    fontSize: 12, padding: '8px 10px', borderRadius: 8,
                                    background: pwMsg.type === 'error' ? 'rgba(211,62,62,0.1)' : 'rgba(46,204,113,0.1)',
                                    color: pwMsg.type === 'error' ? 'var(--danger)' : 'var(--success)',
                                    border: `1px solid ${pwMsg.type === 'error' ? 'rgba(211,62,62,0.3)' : 'rgba(46,204,113,0.3)'}`,
                                }}>
                                    {pwMsg.text}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                                <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={closeChangePassword}>Cancel</button>
                                <button type="submit" className="btn btn-gold" style={{ flex: 1 }} disabled={pwSaving}>
                                    {pwSaving ? 'Saving…' : 'Update Password'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
